'use client';

import React from 'react';

import { LoadingState, type LoadingStateProps } from '../components/loading-state';

export interface UseLoadingStateOptions extends Omit<LoadingStateProps, 'asOverlay'> {
  /**
   * Whether loading state is active
   */
  loading: boolean;
}

/**
 * Hook for inline loading states with overlay capability
 * Provides a LoadingOverlay component that can be used inside any container
 *
 * @example
 * ```tsx
 * const { LoadingOverlay } = useLoadingState({ loading: isLoading, preset: 'kpiCard' });
 *
 * return (
 *   <Box position="relative">
 *     <LoadingOverlay />
 *     {// Your content here}
 *   </Box>
 * );
 * ```
 */
export function useLoadingState({ loading, ...props }: UseLoadingStateOptions) {
  const LoadingOverlay = React.useMemo(
    () => (loading ? () => <LoadingState asOverlay blurBackground {...props} /> : () => null),
    [loading, props]
  );

  return {
    LoadingOverlay,
    isLoading: loading,
  };
}
